"use client"

import {
  Car,
  Cpu,
  MapPin,
  Package,
  Route,
  Settings,
  Timer,
  Truck,
  Users,
  Wrench,
} from "lucide-react"

import { motion } from "motion/react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { NumberTicker } from "@/components/ui/number-ticker"
import VerticalCutReveal from "@/components/ui/fancy/vertical-cut-reveal"

const capabilities = [
  {
    value: 10,
    label: "départements couverts",
    icon: MapPin,
    span: "md:col-span-2",
    bg: "bg-primary/5",
  },
  {
    value: 25,
    label: "tournées de ramassage par semaine",
    icon: Truck,
    span: "",
    bg: "bg-card",
  },
  {
    value: 6,
    label: "affûteuses à commande numérique",
    icon: Cpu,
    span: "",
    bg: "bg-muted/50",
  },
  {
    value: 35,
    label: "machines d'affûtage",
    icon: Settings,
    span: "",
    bg: "bg-card",
  },
  {
    value: 10000,
    label: "clients",
    icon: Users,
    span: "md:col-span-2",
    bg: "bg-primary/5",
  },
  {
    value: 6000,
    label: "références produits",
    icon: Package,
    span: "",
    bg: "bg-muted/50",
  },
  {
    value: 0,
    label: "Partenaire PREMIUM FESTOOL",
    icon: Route,
    span: "",
    bg: "bg-card",
  },
  {
    value: 3000,
    label: "heures de maintenance par an",
    icon: Timer,
    span: "",
    bg: "bg-muted/50",
  },
  {
    value: 0,
    label: "Présence sur toute l'IDF",
    icon: MapPin,
    span: "md:col-span-2",
    bg: "bg-primary/5",
  },
  {
    value: 0,
    label: "Parking client",
    icon: Car,
    span: "",
    bg: "bg-card",
  },
  {
    value: 0,
    label: "SAV électroportatif",
    icon: Wrench,
    span: "",
    bg: "bg-muted/50",
  },
  {
    value: 0,
    label: "Réparateur machines à bois MULTIMARQUES",
    icon: Settings,
    span: "md:col-span-2",
    bg: "bg-primary/5",
  },
] as const

export function CapabilitiesBento() {
  return (
    <section className="section-loose px-4 md:px-6">
      <div className="mx-auto max-w-7xl">
        {/* Section heading */}
        <div className="mb-12">
          <Badge variant="secondary" className="mb-4">
            {"Capacités"}
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl" style={{ textWrap: "balance" }}>
            <VerticalCutReveal
              splitBy="words"
              staggerDuration={0.04}
              staggerFrom="first"
              transition={{ type: "spring", stiffness: 220, damping: 24 }}
            >
              Notre infrastructure au service de votre productivité
            </VerticalCutReveal>
          </h2>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {capabilities.map((cap, index) => {
            const Icon = cap.icon
            return (
              <motion.div
                key={cap.label}
                whileHover={{ y: -4, transition: { type: "spring", stiffness: 320, damping: 22, mass: 0.8 } }}
                className={cn(
                  "rounded-[2rem] p-1.5",
                  "bg-foreground/[0.02] ring-1 ring-foreground/5",
                  "dark:bg-background/[0.06] dark:ring-foreground/10",
                  "transition-[box-shadow,ring-color] duration-300 ease-[var(--ease-premium)]",
                  "hover:shadow-[var(--shadow-md)] hover:ring-foreground/10",
                  cap.span
                )}
                style={{ animationDelay: `${index * 40}ms` }}
              >
                <div
                  className={cn(
                    "flex h-full flex-col rounded-[calc(2rem-0.375rem)] p-4 sm:p-6 md:p-8",
                    cap.bg
                  )}
                >
                  <Icon className="size-5 text-primary" />
                  <p className="mt-4">
                    {cap.value > 0 && (
                      <span className="font-[family-name:var(--font-geist-mono)] tabular-nums text-xl font-bold sm:text-2xl md:text-3xl">
                        <NumberTicker value={cap.value} />
                      </span>
                    )}
                    <span
                      className={cn(
                        "block text-sm text-muted-foreground",
                        cap.value > 0
                          ? "mt-1"
                          : "mt-0 font-medium text-foreground"
                      )}
                    >
                      {cap.label}
                    </span>
                  </p>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
